import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { CONDITION_OPS } from "../constants";
import { analyzeQuery, buildIndexesJson, indexFieldSummary } from "../services/indexAdvisorService";

const OPS = CONDITION_OPS.map((o) => o.id);

export default function IndexAdvisorPage() {
  const { collectionName, selectCollection, allCollections } = useFirestoreAdmin();
  const [clauses, setClauses] = useState([{ field: "", op: "==", value: "" }]);
  const [orderByField, setOrderByField] = useState("");
  const [orderByDir, setOrderByDir] = useState("asc");
  const [analysis, setAnalysis] = useState(null);

  const jsonText = useMemo(
    () => (analysis?.index ? JSON.stringify(buildIndexesJson(analysis.index), null, 2) : ""),
    [analysis]
  );

  const updateClause = (index, patch) => {
    setClauses((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const analyze = () => {
    setAnalysis(analyzeQuery({ collectionName, clauses, orderByField, orderByDir }));
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      toast.success("Copied firestore.indexes.json");
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  const downloadJson = () => {
    const blob = new Blob([jsonText], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "firestore.indexes.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Performance</p>
        <h1 className="text-2xl font-bold">Index Advisor</h1>
        <p className="text-sm text-slate-600">
          Describe a query and find out whether it needs a composite index — then generate the config.
        </p>
      </header>

      <div className="fa-card space-y-3 p-4">
        <select
          className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
          value={collectionName}
          onChange={(e) => selectCollection(e.target.value)}
        >
          {allCollections.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        {clauses.map((c, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              placeholder="Field"
              value={c.field}
              onChange={(e) => updateClause(i, { field: e.target.value })}
            />
            <select
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              value={c.op}
              onChange={(e) => updateClause(i, { op: e.target.value })}
            >
              {OPS.map((op) => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
            <button
              type="button"
              className="fa-btn"
              onClick={() => setClauses((prev) => prev.filter((_, idx) => idx !== i))}
              disabled={clauses.length === 1}
            >
              Remove
            </button>
          </div>
        ))}

        <button type="button" className="fa-btn" onClick={() => setClauses((prev) => [...prev, { field: "", op: "==", value: "" }])}>
          Add filter
        </button>

        <div className="flex flex-wrap items-center gap-2">
          <input
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            placeholder="orderBy field (optional)"
            value={orderByField}
            onChange={(e) => setOrderByField(e.target.value)}
          />
          <select
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
            value={orderByDir}
            onChange={(e) => setOrderByDir(e.target.value)}
          >
            <option value="asc">asc</option>
            <option value="desc">desc</option>
          </select>
          <button type="button" className="fa-btn fa-btn-primary" onClick={analyze} disabled={!collectionName}>
            Analyze
          </button>
        </div>
      </div>

      {analysis && (
        <div className="space-y-4">
          <div
            className={`fa-card p-4 ${analysis.needsComposite ? "border-amber-400" : "border-emerald-400"}`}
            style={{ borderWidth: 2 }}
          >
            <p className="font-semibold">
              {analysis.needsComposite
                ? "⚠ This query needs a composite index"
                : "✓ Single-field indexes cover this query"}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {analysis.needsComposite
                ? "Firestore requires an explicit composite index for this filter/order combination."
                : "Firestore auto-indexes single-field queries — no extra configuration needed."}
            </p>
            {analysis.warnings.map((w, i) => (
              <p key={i} className="mt-2 text-sm text-rose-600">• {w}</p>
            ))}
          </div>

          {analysis.index && (
            <>
              <div className="fa-card fa-table-wrap">
                <table className="fa-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Field</th>
                      <th>Configuration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.index.fields.map((f, i) => (
                      <tr key={f.fieldPath}>
                        <td>{i + 1}</td>
                        <td className="font-mono text-xs">{f.fieldPath}</td>
                        <td className="text-xs">{indexFieldSummary(f)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="fa-card space-y-2 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-semibold">firestore.indexes.json</h2>
                  <div className="flex gap-2">
                    <button type="button" className="fa-btn" onClick={copyJson}>Copy</button>
                    <button type="button" className="fa-btn fa-btn-primary" onClick={downloadJson}>Download</button>
                  </div>
                </div>
                <pre className="overflow-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">{jsonText}</pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
